/**
 * User Data Backup System
 * Automatically backs up user data whenever changes occur
 * Provides data recovery and version management
 */

import { collection, addDoc, query, where, orderBy, limit, getDocs, doc, getDoc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { logSecurityEvent, EVENT_TYPES } from './securityLogger';

// Backup collection name
const BACKUP_COLLECTION = 'user_backups';

// Data fields that should be backed up
export const BACKUP_FIELDS = [
  'pseudo',
  'dp',
  'facebookLink',
  'masterDuelId',
  'edoproPseudo',
  'notificationsEnabled',
  'profilePicture',
  'bannerPicture',
  'division',
  'stats',         // Stats object containing matches, wins, losses, champion
  'tournaments',   // Tournament participation
  'quests',        // Quest progress
  'achievements',  // Achievement data
  'createdAt',
  'lastLoginTime'
];

// Fields that are calculated and don't need backup
export const CALCULATED_FIELDS = [
  'YP', // Calculated from wins
  'winRate', // Calculated from wins/matches
  'rank' // Calculated from other stats
];

/**
 * Create a current state backup (for emergency recovery)
 * @param {string} userId - User ID
 * @param {Object} userData - Current user data
 */
export const createCurrentStateBackup = async (userId, userData) => {
  try {
    // Extract only the fields that should be backed up
    const backupData = {};
    BACKUP_FIELDS.forEach(field => {
      if (userData.hasOwnProperty(field)) {
        backupData[field] = userData[field];
      }
    });

    // Create current state backup entry
    const backupEntry = {
      userId,
      backupData,
      changeType: 'current_state',
      changeDetails: {
        description: 'Current state backup for emergency recovery',
        isCurrentState: true
      },
      triggeredBy: 'system',
      timestamp: new Date(),
      version: 'current',
      dataHash: generateDataHash(backupData),
      isActive: true,
      isCurrentState: true
    };

    // Check if current state backup already exists
    const currentStateQuery = query(
      collection(db, BACKUP_COLLECTION),
      where('userId', '==', userId),
      where('isCurrentState', '==', true)
    );
    const currentStateSnapshot = await getDocs(currentStateQuery);
    
    if (currentStateSnapshot.empty) {
      // Create new current state backup
      const backupRef = await addDoc(collection(db, BACKUP_COLLECTION), backupEntry);
      console.log('✅ Current state backup created:', backupRef.id);
    } else {
      // Update existing current state backup
      const currentStateDoc = currentStateSnapshot.docs[0];
      await updateDoc(doc(db, BACKUP_COLLECTION, currentStateDoc.id), {
        backupData,
        timestamp: new Date(),
        dataHash: generateDataHash(backupData)
      });
      console.log('✅ Current state backup updated:', currentStateDoc.id);
    }
    
    return { success: true };
  } catch (error) {
    console.error('❌ Failed to create current state backup:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Create a backup of user data
 * @param {string} userId - User ID
 * @param {Object} userData - Current user data
 * @param {string} changeType - Type of change (e.g., 'dp_purchase', 'match_result', 'profile_update')
 * @param {Object} changeDetails - Details about what changed
 * @param {string} triggeredBy - What triggered the backup (e.g., 'user_action', 'admin_action', 'system')
 */
export const createUserBackup = async (userId, userData, changeType, changeDetails = {}, triggeredBy = 'user_action') => {
  try {
    // Extract only the fields that should be backed up
    const backupData = {};
    BACKUP_FIELDS.forEach(field => {
      if (userData.hasOwnProperty(field)) {
        backupData[field] = userData[field];
      }
    });

    // Create backup entry
    const backupEntry = {
      userId,
      backupData,
      changeType,
      changeDetails,
      triggeredBy,
      timestamp: new Date(),
      version: await getNextBackupVersion(userId),
      dataHash: generateDataHash(backupData),
      isActive: true
    };

    // Store backup in Firestore
    const backupRef = await addDoc(collection(db, BACKUP_COLLECTION), backupEntry);
    
    // Update current state backup with the new data
    await createCurrentStateBackup(userId, userData);
    
    // Clean up old backups to keep only the 3 most recent
    await cleanupOldBackups(userId);
    
    // Log backup creation
    await logSecurityEvent(EVENT_TYPES.DATA_BACKUP_CREATED, userId, {
      backupId: backupRef.id,
      changeType,
      triggeredBy,
      dataSize: JSON.stringify(backupData).length
    });

    console.log(`✅ User backup created for ${userId}:`, {
      backupId: backupRef.id,
      changeType,
      version: backupEntry.version
    });

    return {
      success: true,
      backupId: backupRef.id,
      version: backupEntry.version
    };

  } catch (error) {
    console.error('❌ Failed to create user backup:', error);
    
    // Log backup failure
    await logSecurityEvent(EVENT_TYPES.DATA_BACKUP_FAILED, userId, {
      changeType,
      error: error.message,
      triggeredBy
    });

    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get the next backup version number for a user
 */
const getNextBackupVersion = async (userId) => {
  try {
    const backupsQuery = query(
      collection(db, BACKUP_COLLECTION),
      where('userId', '==', userId),
      orderBy('version', 'desc'),
      limit(1)
    );
    
    const snapshot = await getDocs(backupsQuery);
    
    if (snapshot.empty) {
      return 1; // First backup
    }
    
    const latestBackup = snapshot.docs[0].data();
    return (latestBackup.version || 0) + 1;
  } catch (error) {
    console.warn('Could not determine backup version, using timestamp:', error);
    return Date.now();
  }
};

/**
 * Generate a hash for data integrity verification
 */
const generateDataHash = (data) => {
  const dataString = JSON.stringify(data, Object.keys(data).sort());
  let hash = 0;
  for (let i = 0; i < dataString.length; i++) {
    const char = dataString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return hash.toString(16);
};

/**
 * Get user's backup history
 * @param {string} userId - User ID
 * @param {number} limit - Maximum number of backups to return
 */
export const getUserBackupHistory = async (userId, limitCount = 50) => {
  try {
    // Use a simpler query that doesn't require a composite index
    const backupsQuery = query(
      collection(db, BACKUP_COLLECTION),
      where('userId', '==', userId),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(backupsQuery);
    const backups = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate?.() || new Date()
    }));
    
    // Sort by timestamp in JavaScript (since we can't use orderBy with composite index)
    backups.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    
    return {
      success: true,
      backups
    };
  } catch (error) {
    console.error('Failed to get backup history:', error);
    return {
      success: false,
      error: error.message,
      backups: []
    };
  }
};

/**
 * Restore user data from a specific backup
 * @param {string} userId - User ID
 * @param {string} backupId - Backup ID to restore from
 */
export const restoreUserFromBackup = async (userId, backupId) => {
  try {
    // Get the backup data
    const backupDoc = await getDoc(doc(db, BACKUP_COLLECTION, backupId));
    
    if (!backupDoc.exists()) {
      throw new Error('Backup not found');
    }
    
    const backupData = backupDoc.data();
    
    if (backupData.userId !== userId) {
      throw new Error('Backup does not belong to this user');
    }
    
    // Create a new backup of current data before restoring
    const currentUserDoc = await getDoc(doc(db, 'users', userId));
    if (currentUserDoc.exists()) {
      await createUserBackup(
        userId, 
        currentUserDoc.data(), 
        'pre_restore_backup', 
        { restoredFrom: backupId },
        'system'
      );
    }
    
    // Log restoration attempt
    await logSecurityEvent(EVENT_TYPES.DATA_RESTORED, userId, {
      backupId,
      backupVersion: backupData.version,
      changeType: backupData.changeType
    });
    
    return {
      success: true,
      backupData: backupData.backupData,
      version: backupData.version,
      timestamp: backupData.timestamp
    };
    
  } catch (error) {
    console.error('Failed to restore from backup:', error);
    
    await logSecurityEvent(EVENT_TYPES.DATA_RESTORE_FAILED, userId, {
      backupId,
      error: error.message
    });
    
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get the latest backup for a user
 */
export const getLatestUserBackup = async (userId) => {
  try {
    // Use a simpler query and sort in JavaScript
    const backupsQuery = query(
      collection(db, BACKUP_COLLECTION),
      where('userId', '==', userId),
      limit(10) // Get more recent backups and sort in JS
    );
    
    const snapshot = await getDocs(backupsQuery);
    
    if (snapshot.empty) {
      return {
        success: true,
        backup: null
      };
    }
    
    // Sort by timestamp and get the latest
    const backups = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate?.() || new Date()
    }));
    
    backups.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    const latestBackup = backups[0];
    
    return {
      success: true,
      backup: latestBackup
    };
  } catch (error) {
    console.error('Failed to get latest backup:', error);
    return {
      success: false,
      error: error.message,
      backup: null
    };
  }
};

/**
 * Create current state backups for all users (admin function)
 */
export const createCurrentStateBackupsForAllUsers = async () => {
  try {
    console.log('🚀 Creating current state backups for all users...');
    
    // Get all users
    const usersSnapshot = await getDocs(collection(db, 'users'));
    const users = usersSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    let successful = 0;
    let failed = 0;
    
    // Create current state backup for each user
    for (const user of users) {
      try {
        await createCurrentStateBackup(user.id, user);
        successful++;
        console.log(`✅ Current state backup created for user ${user.id}`);
      } catch (error) {
        console.error(`❌ Failed to create current state backup for user ${user.id}:`, error);
        failed++;
      }
    }
    
    console.log(`🎉 Current state backup creation completed: ${successful} successful, ${failed} failed`);
    
    return {
      success: true,
      successful,
      failed,
      total: users.length
    };
  } catch (error) {
    console.error('❌ Failed to create current state backups:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Create initial backup for all users (admin function)
 */
export const createInitialBackupsForAllUsers = async () => {
  try {
    console.log('🔄 Starting initial backup for all users...');
    
    const usersSnapshot = await getDocs(collection(db, 'users'));
    const backupPromises = [];
    let successCount = 0;
    let errorCount = 0;
    
    for (const userDoc of usersSnapshot.docs) {
      const userData = userDoc.data();
      const userId = userDoc.id;
      
      const backupPromise = createUserBackup(
        userId,
        userData,
        'initial_backup',
        { 
          totalUsers: usersSnapshot.size,
          backupDate: new Date().toISOString()
        },
        'system'
      ).then(result => {
        if (result.success) {
          successCount++;
        } else {
          errorCount++;
          console.error(`Failed to backup user ${userId}:`, result.error);
        }
      });
      
      backupPromises.push(backupPromise);
    }
    
    await Promise.all(backupPromises);
    
    console.log(`✅ Initial backup completed: ${successCount} successful, ${errorCount} failed`);
    
    return {
      success: true,
      totalUsers: usersSnapshot.size,
      successful: successCount,
      failed: errorCount
    };
    
  } catch (error) {
    console.error('❌ Failed to create initial backups:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Clean up all user backups to keep only 3 most recent per user (admin function)
 */
export const cleanupAllUserBackups = async () => {
  try {
    console.log('🧹 Starting cleanup of all user backups...');
    
    // Get all users
    const usersSnapshot = await getDocs(collection(db, 'users'));
    let totalCleaned = 0;
    let usersProcessed = 0;
    
    for (const userDoc of usersSnapshot.docs) {
      const userId = userDoc.id;
      
      // Get all backups for this user
      const backupsQuery = query(
        collection(db, BACKUP_COLLECTION),
        where('userId', '==', userId)
      );
      
      const backupsSnapshot = await getDocs(backupsQuery);
      
      if (backupsSnapshot.empty) {
        continue; // No backups for this user
      }
      
      // Convert to array and sort by timestamp (newest first)
      const backups = backupsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate?.() || new Date()
      }));
      
      backups.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      
      // Keep only the 3 most recent backups
      const backupsToDelete = backups.slice(3);
      
      if (backupsToDelete.length > 0) {
        // Delete old backups
        const deletePromises = backupsToDelete.map(backup => 
          deleteDoc(doc(db, BACKUP_COLLECTION, backup.id))
        );
        
        await Promise.all(deletePromises);
        totalCleaned += backupsToDelete.length;
        
        console.log(`🧹 Cleaned up ${backupsToDelete.length} old backups for user ${userId}`);
      }
      
      usersProcessed++;
    }
    
    console.log(`✅ Cleanup completed: ${totalCleaned} backups deleted across ${usersProcessed} users`);
    
    return {
      success: true,
      totalCleaned,
      usersProcessed
    };
    
  } catch (error) {
    console.error('❌ Failed to cleanup user backups:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Backup user data with automatic change detection
 * This function should be called whenever user data is modified
 */
export const backupUserDataChange = async (userId, oldData, newData, changeType, changeDetails = {}) => {
  try {
    // Detect what actually changed
    const changes = detectDataChanges(oldData, newData);
    
    if (changes.length === 0) {
      console.log('No changes detected, skipping backup');
      return { success: true, skipped: true };
    }
    
    // Create backup with change details
    const result = await createUserBackup(
      userId,
      newData,
      changeType,
      {
        ...changeDetails,
        changes,
        oldData: oldData
      },
      'user_action'
    );
    
    return result;
  } catch (error) {
    console.error('Failed to backup user data change:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Detect what fields changed between old and new data
 */
const detectDataChanges = (oldData, newData) => {
  const changes = [];
  
  BACKUP_FIELDS.forEach(field => {
    const oldValue = oldData[field];
    const newValue = newData[field];
    
    if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
      changes.push({
        field,
        oldValue,
        newValue,
        type: typeof newValue
      });
    }
  });
  
  return changes;
};

/**
 * Clean up old backups to keep only the 3 most recent ones
 * @param {string} userId - User ID
 */
const cleanupOldBackups = async (userId) => {
  try {
    // Get all backups for the user
    const backupsQuery = query(
      collection(db, BACKUP_COLLECTION),
      where('userId', '==', userId)
    );
    
    const snapshot = await getDocs(backupsQuery);
    
    if (snapshot.empty) {
      return; // No backups to clean up
    }
    
    // Convert to array and sort by timestamp (newest first)
    const backups = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate?.() || new Date()
    }));
    
    backups.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    
    // Separate current state backup from historical backups
    const currentStateBackup = backups.find(backup => backup.isCurrentState);
    const historicalBackups = backups.filter(backup => !backup.isCurrentState);
    
    // Keep current state backup + 3 most recent historical backups
    const backupsToKeep = currentStateBackup ? 4 : 3; // 1 current + 3 historical, or just 3 if no current state
    const backupsToDelete = historicalBackups.slice(3);
    
    // Also delete old current state backups if there are multiple
    if (currentStateBackup) {
      const duplicateCurrentStates = backups.filter(backup => 
        backup.isCurrentState && backup.id !== currentStateBackup.id
      );
      backupsToDelete.push(...duplicateCurrentStates);
    }
    
    if (backupsToDelete.length === 0) {
      return; // No cleanup needed
    }
    
    // Delete old backups
    const deletePromises = backupsToDelete.map(backup => 
      deleteDoc(doc(db, BACKUP_COLLECTION, backup.id))
    );
    
    await Promise.all(deletePromises);
    
    console.log(`🧹 Cleaned up ${backupsToDelete.length} old backups for user ${userId}`);
    
    // Log cleanup
    await logSecurityEvent(EVENT_TYPES.DATA_BACKUP_CLEANUP, userId, {
      deletedCount: backupsToDelete.length,
      remainingCount: backupsToKeep,
      hasCurrentState: !!currentStateBackup
    });
    
  } catch (error) {
    console.error('Failed to cleanup old backups:', error);
    // Don't throw error - cleanup failure shouldn't break backup creation
  }
};

/**
 * Get backup statistics for a user
 */
export const getUserBackupStats = async (userId) => {
  try {
    const result = await getUserBackupHistory(userId, 100); // Limit to 100 for performance
    
    if (!result.success) {
      return result;
    }
    
    const backups = result.backups;
    const stats = {
      totalBackups: backups.length,
      latestBackup: backups[0]?.timestamp || null,
      oldestBackup: backups[backups.length - 1]?.timestamp || null,
      changeTypes: {},
      dataSize: 0
    };
    
    // Analyze change types
    backups.forEach(backup => {
      const changeType = backup.changeType || 'unknown';
      stats.changeTypes[changeType] = (stats.changeTypes[changeType] || 0) + 1;
      stats.dataSize += JSON.stringify(backup.backupData).length;
    });
    
    return {
      success: true,
      stats
    };
    
  } catch (error) {
    console.error('Failed to get backup stats:', error);
    return {
      success: false,
      error: error.message
    };
  }
};
